import { useCallback, useEffect, useState } from 'react';
import {
    withActionBarKeys,
    withActionBarKeyAssigned,
    createDefaultActionBarKeys,
    findActionBarKeyReservation,
    formatKeyLabel,
    isAssignableActionBarKey,
    MODIFIER_KEYS,
    type DigitalInputBindingId,
} from '@sorrem/shared';
import { getPageInput } from '@sorrem-client/ui/input/page-input';
import { useAccountSetting } from './use-account-setting';

const DEFAULT_KEYS: string[] = createDefaultActionBarKeys();

/** Every digital binding but the action bar's own: each may own a key the action bar can't take. */
type KeyReservingBindingId = Exclude<DigitalInputBindingId, `action-bar-${number}`>;

/** What each binding that can own a key does, for "that key is taken" messages; a new binding must say. */
const RESERVED_KEY_ACTIONS: Readonly<Record<KeyReservingBindingId, string>> = {
    'primary-action': 'moving and attacking',
    'aimed-attack': 'aimed attacks',
    'offhand-attack': 'off-hand attacks',
    'force-move': 'force-move',
    'dodge': 'dodge',
    'open-menu': 'the menu',
    'toggle-commission': 'the commission',
    'toggle-map': 'the map',
    'toggle-inventory': 'the inventory',
    'toggle-vessel': 'the Essence Vessel',
    'show-party': 'the party',
    'show-chat': 'chat',
    'toggle-combat-log': 'the combat log',
    'open-skills': 'skills',
    'open-crucibles': 'Crucibles',
    'open-crafting': 'crafting',
    'open-journey': 'the journey',
    'report-bug': 'bug reports',
    'take-snapshot': 'developer snapshots',
    'take-clean-render': 'developer renders',
};

function isKeyReservingBindingId(id: DigitalInputBindingId): id is KeyReservingBindingId {
    return !id.startsWith('action-bar-');
}

interface ActionBarKeysState {
    /** The key on each action-bar slot; the defaults until settings load. */
    keys: readonly string[];
    /** Moves slot `index` to `key`, swapping with the slot that had it, and saves (applied optimistically). */
    setKey: (index: number, key: string) => void;
}

/** The key that presses each action-bar slot (an account setting). */
export function useActionBarKeys(): ActionBarKeysState {
    const { value: keys, set } = useAccountSetting('actionBarKeys', DEFAULT_KEYS);

    const setKey = useCallback((index: number, key: string): void => {
        set((current) => withActionBarKeyAssigned(current, index, key));
    }, [set]);

    return { keys, setKey };
}

/** Binds the action-bar slots to the account's keys for this page, and again whenever they change. */
export function useSyncActionBarKeys(): void {
    // `keys` keeps its identity until the settings change (cached query data, or the module defaults).
    // Joining them into a string key instead broke on keys like `|`.
    const { keys } = useActionBarKeys();
    useEffect(() => {
        const input = getPageInput();
        input.setBindings(withActionBarKeys(input.getBindings(), keys));
    }, [keys]);
}

/** Why `key` can't go on an action-bar slot, for the player; undefined when it can. */
export function findActionBarKeyProblem(key: string): string | undefined {
    if (isAssignableActionBarKey(key)) {
        return undefined;
    }
    const reservation = findActionBarKeyReservation(key);
    const label = formatKeyLabel(key);
    if (reservation !== undefined) {
        return `${label} is taken: it opens ${isKeyReservingBindingId(reservation) ? RESERVED_KEY_ACTIONS[reservation] : 'something else'}.`;
    }
    return `${label} can't go on the action bar. Use a letter, number or punctuation key.`;
}

interface ActionBarKeyRebindingState {
    /** The slot waiting for its new key, or null. */
    rebindingIndex: number | null;
    /** Why the last key pressed was refused, while waiting; null otherwise. */
    refusal: string | null;
    /** Waits for the next key press to move slot `index` onto; calling it again for the same slot stops waiting. */
    toggleRebinding: (index: number) => void;
}

/**
 * Moving an action-bar slot to another key: after `toggleRebinding`, the next
 * key pressed goes on the slot (Esc stops waiting). The key is claimed before
 * anything else sees it, so pressing I to try it doesn't also open the
 * inventory. A key another binding owns is refused with the reason.
 */
export function useActionBarKeyRebinding(): ActionBarKeyRebindingState {
    const { setKey } = useActionBarKeys();
    const [rebindingIndex, setRebindingIndex] = useState<number | null>(null);
    const [refusal, setRefusal] = useState<string | null>(null);

    const toggleRebinding = useCallback((index: number): void => {
        setRefusal(null);
        setRebindingIndex((current) => (current === index ? null : index));
    }, []);

    useEffect(() => {
        if (rebindingIndex === null) {
            return undefined;
        }
        const handleKeyDown = (event: KeyboardEvent): void => {
            if ((MODIFIER_KEYS as readonly string[]).includes(event.key) || event.ctrlKey || event.metaKey || event.altKey) {
                return;
            }
            event.preventDefault();
            event.stopImmediatePropagation();
            if (event.key === 'Escape') {
                setRebindingIndex(null);
                setRefusal(null);
                return;
            }
            const reason = findActionBarKeyProblem(event.key);
            if (reason !== undefined) {
                setRefusal(reason);
                return;
            }
            setKey(rebindingIndex, event.key);
            setRebindingIndex(null);
            setRefusal(null);
        };
        window.addEventListener('keydown', handleKeyDown, { capture: true });
        return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
    }, [rebindingIndex, setKey]);

    return { rebindingIndex, refusal, toggleRebinding };
}
